/** @author PVCOMBANK */
import * as React from "react";
import { Paragraph, useTheme, Checkbox } from "react-native-paper";
import { RegisterOptions, useForm } from "react-hook-form";
import { Button } from "react-native-paper";
import { Block } from "@block";
import { useDesktopMediaQuery } from "@media-query";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { InputAutoComplete, InputText, InputTextCurrency } from "app/shared/components/form/input";
import { StyleSheet, Linking } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { ACCOUNT, RESPONSEDATA } from "app/shared/services/services-model.type";
import {
  fetchTermDepositMaturityRequest,
  fetchTermDepositInfoRequest,
  fetchTermDepositTermRequest,
  setTermOpenStore,
} from "../../reducer/actions";
import { DataTermDepositCreate } from "../../reducer/types";
import {
  getTermDepositMaturitySelector,
  getTermDepositOpenPendingSelector,
  getTermDepositOpenSelector,
  getTermDepositTermSelector,
} from "../../reducer/selectors";
import {
  LINK_TERM,
  getTermDepositInfoData,
  getTermDepositListMaturity,
  getTermDepositListTerm,
  loadBranchTermDeposit,
} from "app/modules/term-deposit/term-deposit-utils/term-deposit.utils";
import { formatCurrency } from "app/shared/util/format-currency";
import { ItemCompleteProps } from "app/shared/components/modal/auto-complete/auto-complete";
import { matchSpecialCharacter } from "app/shared/util/validate";

export interface FormTermDepositOpenProps {
  accountDebit: ACCOUNT;
  termInfo?: RESPONSEDATA;
  onSubmit: Function;
  onCancel: Function;
  requestCreate: Function;
}

enum FormEnum {
  amount,
  branchCode,
  branchName,
  productCode,
  productName,
  termCode,
  termName,
  maturityCode,
  maturityName,
  promoCode,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export type FormTermDepositOpenData = {
  amount: string;
  branchCode: string;
  branchName: string;
  productCode: string;
  productName: string;
  termCode: string;
  termName: string;
  maturityCode: string;
  maturityName: string;
  promoCode: string;
};

const FormTermDeposit = (props: FormTermDepositOpenProps) => {
  const { colors, fonts } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDesktop = useDesktopMediaQuery();
  // Instance Form Method
  const { control, handleSubmit, getValues, setValue, trigger, clearErrors } = useForm<FormTermDepositOpenData>({
    defaultValues: {
      amount: "",
      branchCode: "",
      branchName: "",
      productCode: "",
      productName: "",
      termCode: "",
      termName: "",
      maturityCode: "",
      maturityName: "",
      promoCode: "",
    },
    mode: "onChange",
  });

  const pendingInfo = useSelector(getTermDepositOpenPendingSelector);
  const termStore = useSelector(getTermDepositTermSelector);
  const termOpenStore = useSelector(getTermDepositOpenSelector);
  const maturityStore = useSelector(getTermDepositMaturitySelector);

  const { accountDebit, termInfo, onSubmit, requestCreate, onCancel } = props;
  const [branchArray, setBranch] = React.useState<ItemCompleteProps[]>([{ value: "", label: "" }]);
  const [productArray, setProductArray] = React.useState<ItemCompleteProps[]>([{ value: "", label: "" }]);
  const [termArray, setTermArray] = React.useState<ItemCompleteProps[]>([{ value: "", label: "" }]);
  const [maturityTypeArray, setMaturityTypeArray] = React.useState<ItemCompleteProps[]>([{ value: "", label: "" }]);
  const [validateAmountMin, setAmountMin] = React.useState(100000);
  const [isNotSelectTerm, setIsNotSelectTerm] = React.useState<boolean>(true);
  const [isNotSelectMaturity, setIsNotSelectMaturity] = React.useState<boolean>(true);
  const [checked, setChecked] = React.useState<boolean>(false);
  const [loadProductByPromoCode, setLoadProductByPromoCode] = React.useState<boolean>(false);
  const [loadTermByPromoCode, setLoadTermByPromoCode] = React.useState<boolean>(false);
  const [loadMaturityByPromoCode, setLoadMaturityByPromoCode] = React.useState<boolean>(false);
  const [loadBranchByPromoCode, setLoadBranchByPromoCode] = React.useState<boolean>(false);

  // Styles Component
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        fieldFromText: {
          color: colors.text,
          width: 200,
          fontWeight: "700",
          fontSize: 16,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        acceptButtonContainer: { width: 128, alignSelf: "center", opacity: checked ? 1 : 0.5 },
        textNote: { ...fonts.semibold, color: colors.warning, fontSize: 14 },
        textTerm: { ...fonts.semibold, color: colors.text, fontSize: 15 },
        textTerm2: { ...fonts.regular, textDecorationLine: "underline", fontStyle: "italic" },
      }),
    [colors, fonts, checked],
  );

  // Tạo validate Schema
  const validateSchemeForm: ValidDataSchemeFormData = {
    amount: {
      required: {
        value: true,
        message: t("SAVING_LOI_CHUA_NHAP_SO_TIEN"),
      },
      validate: {
        amountMin: (value: string) => {
          if (value.length && Number(value) < validateAmountMin) {
            const msg = t("SAVING_SO_TIEN_TOI_THIEU");
            return msg + " " + formatCurrency(String(validateAmountMin));
          }
          return true;
        },
        amountMax: (value: string) => {
          if (accountDebit?.AVAILABLEBALANCE?.length && value.length && Number(value) > Number(accountDebit?.AVAILABLEBALANCE)) {
            return t("ACC_SO_DU_KHA_DUNG_NOT_ENOUGH").toString();
          }
          return true;
        },
      },
    },
    productCode: {
      required: {
        value: true,
        message: t("SAVING_LOI_CHUA_CHON_SAN_PHAM"),
      },
    },
    productName: {
      required: {
        value: false,
        message: "",
      },
    },
    termCode: {
      required: {
        value: true,
        message: t("SAVING_LOI_CHUA_CHON_KY_HAN"),
      },
    },
    termName: {
      required: {
        value: false,
        message: "",
      },
    },
    maturityCode: {
      required: {
        value: true,
        message: t("SAVING_LOI_CHUA_CHON_HINH_THUC_DAO_HAN"),
      },
    },
    maturityName: {
      required: {
        value: false,
        message: "",
      },
    },
    branchCode: {
      required: {
        value: true,
        message: t("SAVING_LOI_CHUA_CHON_CHI_NHANH_QUAN_LY_SO"),
      },
    },
    branchName: {
      required: {
        value: false,
        message: "",
      },
    },
    promoCode: {
      required: {
        value: false,
        message: "",
      },
      validate: {
        regex: (value: string) => {
          if (value.length && matchSpecialCharacter(value)) {
            return t("SAVING_LOI_KY_TU_DAC_BIET").toString();
          }
          return true;
        },
      },
    },
  };

  const handleRequestCreate = (value: FormTermDepositOpenData): void => {
    const dataUpdate: DataTermDepositCreate = {
      AMOUNT: value.amount,
      BRANCH: { value: value.branchCode, label: value.branchName },
      PRODUCT: { value: value.productCode, label: value.productName },
      TERM: { value: value.termCode, label: value.termName },
      MATURITY: { value: value.maturityCode, label: value.maturityName },
      PROMO_CODE: value.promoCode,
    };
    console.log("dataUpdate: ", dataUpdate);
    requestCreate(dataUpdate);
  };

  const onPressCreate = () => {
    onSubmit();
    handleSubmit(handleRequestCreate).call(() => {});
  };

  const onPressCancel = () => {
    onCancel();
  };

  const onClickSelectBranch = () => {
    if (!pendingInfo && !branchArray.length) {
      dispatch(fetchTermDepositInfoRequest());
    }
  };
  const onChangeBranch = (item?: ItemCompleteProps) => {
    setValue("branchCode", item?.value || "");
    setValue("branchName", item?.label || "");
  };

  const onClickSelectProduct = () => {
    if (!pendingInfo && !productArray.length) {
      dispatch(fetchTermDepositInfoRequest());
    }
  };
  const onChangeProduct = (item?: ItemCompleteProps) => {
    setValue("productCode", item?.value || "");
    setValue("productName", item?.label || "");
    setValue("termCode", "");
    setValue("termName", "");
    setValue("maturityCode", "");
    setValue("maturityName", "");
    clearErrors("termCode");
    clearErrors("maturityCode");
    clearErrors("amount");
    dispatch(fetchTermDepositTermRequest(item?.value));
  };

  const onClickSelectTerm = async () => {
    const triggerProduct = await trigger("productCode");
    if (triggerProduct && !pendingInfo && !termArray.length) {
      const productCode = getValues("productCode");
      dispatch(fetchTermDepositTermRequest(productCode));
    }
  };
  const onChangeTerm = (item?: ItemCompleteProps) => {
    console.log("onChangeTerm: ", item);
    console.log("termName: ", item?.name);
    setValue("termCode", item?.value || "");
    setValue("termName", item?.name || "");
    setValue("maturityCode", "");
    setValue("maturityName", "");
    clearErrors("maturityCode");
    clearErrors("amount");
    if (item?.other?.length) {
      setAmountMin(Number(item.other.toString()));
      trigger("amount");
    }
    const textSearch = getValues("productCode") + "-" + item?.value + "-" + item?.name || "";
    dispatch(fetchTermDepositMaturityRequest(textSearch));
  };

  const onClickSelectMatType = async () => {
    const triggerProduct = await trigger("productCode");
    const triggerTerm = await trigger("termCode");
    if (triggerProduct && triggerTerm && !pendingInfo && !maturityTypeArray.length) {
      const textSearch = getValues("productCode") + "-" + getValues("termCode") + "-" + getValues("termName");
      dispatch(fetchTermDepositMaturityRequest(textSearch));
    }
  };
  const onChangeMatType = (item?: ItemCompleteProps) => {
    setValue("maturityCode", item?.value || "");
    setValue("maturityName", item?.label || "");
  };

  const handlerChangeDataByPromoCode = React.useCallback(
    (data: DataTermDepositCreate) => {
      setValue("amount", data.AMOUNT || "");
      setValue("promoCode", data.PROMO_CODE || "");
      if (data.PRODUCT?.value != getValues("productCode")) {
        setLoadProductByPromoCode(true);
      }
    },
    [getValues, setValue],
  );

  const onClickShowTermLink = () => {
    Linking.openURL(LINK_TERM);
  };

  /// MARK: - Child Component
  const FieldForm = (
    <Block>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("TRANFER_TAI_KHOAN_NHAN")}</Paragraph>
      <StackSpacing size="medium" />
    </Block>
  );

  const FormCheckTerm = (
    <Block center={isDesktop ? true : false} flex={1} pt={spacing.medium}>
      <Block row>
        <StackSpacing size="medium" />
        <Checkbox
          status={checked ? "checked" : "unchecked"}
          onPress={() => {
            setChecked(!checked);
          }}
        />
        <Paragraph style={styles.textTerm}>
          {t("SAVING_NOTE_DIEU_KHOAN_DIEU_KIEN_1")}{" "}
          <Paragraph style={[styles.textTerm, styles.textTerm2]} onPress={onClickShowTermLink}>
            {t("SAVING_NOTE_DIEU_KHOAN_DIEU_KIEN_2")}
          </Paragraph>{" "}
          {t("SAVING_NOTE_DIEU_KHOAN_DIEU_KIEN_3")}
        </Paragraph>
        <StackSpacing size="medium" />
      </Block>
      <Block row pt={spacing.medium}>
        <StackSpacing size="medium" />
        <Paragraph style={styles.textNote}>{t("SAVING_NOTE_CUT_OFF_TIME_OPEN")}</Paragraph>
        <StackSpacing size="medium" />
      </Block>
    </Block>
  );

  const FormInput = (
    <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
      <InputTextCurrency
        name="amount"
        keyboardType={"numeric"}
        control={control}
        rules={validateSchemeForm.amount}
        label={t("TOPUP_SO_TIEN")}
        rightCurrency={t("COMMON_VND")}
      />
      <InputAutoComplete
        control={control}
        name="productCode"
        options={productArray}
        pending={pendingInfo}
        rules={validateSchemeForm.productCode}
        title={t("SAVING_CHON_SAN_PHAM")}
        label={t("SAVING_SAN_PHAM")}
        onClick={onClickSelectProduct}
        onDismiss={onChangeProduct}
        showSearch
      />
      <Block row={isDesktop ? true : false} flex={1}>
        <Block flex={isDesktop ? 1 : undefined}>
          <InputAutoComplete
            control={control}
            name="termCode"
            options={termArray}
            pending={pendingInfo}
            rules={validateSchemeForm.termCode}
            title={t("SAVING_CHON_KY_HAN")}
            label={t("SAVING_KY_HAN")}
            onClick={onClickSelectTerm}
            checkSelect={isNotSelectTerm}
            onDismiss={onChangeTerm}
            showSearch
          />
        </Block>
        <QueueSpacing size="huge" />
        <Block flex={isDesktop ? 2 : undefined}>
          <InputAutoComplete
            control={control}
            name="maturityCode"
            options={maturityTypeArray}
            pending={pendingInfo}
            rules={validateSchemeForm.maturityCode}
            title={t("SAVING_CHON_HINH_THUC_DAO_HAN")}
            label={t("SAVING_HINH_THUC_DAO_HAN")}
            onClick={onClickSelectMatType}
            checkSelect={isNotSelectMaturity}
            onDismiss={onChangeMatType}
            showSearch
          />
        </Block>
      </Block>

      <InputAutoComplete
        control={control}
        name="branchCode"
        options={branchArray}
        pending={pendingInfo}
        rules={validateSchemeForm.branchCode}
        title={t("SAVING_CHON_CHI_NHANH_QUAN_LY_SO")}
        label={t("SAVING_CHI_NHANH_QUAN_LY_SO")}
        onClick={onClickSelectBranch}
        onDismiss={onChangeBranch}
        showSearch
      />
      <InputText name="promoCode" control={control} rules={validateSchemeForm.promoCode} label={t("SAVING_PROMO_CODE")} />
    </Block>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <Button mode="contained" style={styles.buttonContainer} onPress={onPressCancel}>
        {t("COMMON_HUY")}
      </Button>
      <QueueSpacing size="large" />
      <Button mode="contained" style={styles.acceptButtonContainer} onPress={onPressCreate} disabled={!checked}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  // Map data theo mã ưu đãi
  React.useEffect(() => {
    if (
      termOpenStore.dataCreate &&
      termOpenStore.isChangeDataByPromoCode == true &&
      termOpenStore.responseCreate?.EBACCOUNT_DEPOSITS?.MATCH == "false"
    ) {
      handlerChangeDataByPromoCode(termOpenStore.dataCreate);
      dispatch(setTermOpenStore({ ...termOpenStore, isChangeDataByPromoCode: false }));
    }
  }, [dispatch, handlerChangeDataByPromoCode, termOpenStore, termOpenStore.isChangeDataByPromoCode]);

  // Lấy danh sách sản phẩm / chi nhánh quản lý
  React.useEffect(() => {
    if (termInfo) {
      loadBranchTermDeposit().then(() => {
        const { listProduct, listBranch } = getTermDepositInfoData(termInfo);
        setProductArray(listProduct);
        setBranch(listBranch);
      });
    }
  }, [termInfo]);

  // Lấy danh sách kỳ hạn
  React.useEffect(() => {
    const listTerm = getTermDepositListTerm(termStore);
    if (termStore) {
      setIsNotSelectTerm(false);
      setIsNotSelectMaturity(true);
    }
    setTermArray(listTerm);
  }, [termStore]);

  // Lấy danh sách hình thức đáo hạn
  React.useEffect(() => {
    const listMaturity = getTermDepositListMaturity(maturityStore);
    if (maturityStore) {
      setIsNotSelectMaturity(false);
    }
    setMaturityTypeArray(listMaturity);
  }, [maturityStore]);

  // load danh sách Sản phẩm
  React.useEffect(() => {
    if (loadProductByPromoCode === true) {
      onChangeProduct(termOpenStore.dataCreate?.PRODUCT);
      setLoadProductByPromoCode(false);
      if (termOpenStore.dataCreate?.TERM?.value != getValues("termCode")) {
        console.log("setLoadTermByPromoCode true");

        setLoadTermByPromoCode(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadProductByPromoCode]);

  // load danh sách kỳ hạn
  React.useEffect(() => {
    if (loadTermByPromoCode === true && termStore) {
      console.log("load danh sách kỳ hạn");

      onChangeTerm({
        value: termOpenStore.dataCreate?.TERM?.value || "",
        label: termOpenStore.dataCreate?.TERM?.label || "",
        name: termOpenStore.dataCreate?.TERM?.label || "",
      });
      setLoadTermByPromoCode(false);
      if (termOpenStore.dataCreate?.MATURITY?.value != getValues("maturityCode")) {
        setLoadMaturityByPromoCode(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTermByPromoCode, termStore]);
  // load hình thức đáo hạn
  React.useEffect(() => {
    if (loadMaturityByPromoCode === true && maturityStore) {
      onChangeMatType(termOpenStore.dataCreate?.MATURITY);
      setLoadMaturityByPromoCode(false);
      if (termOpenStore.dataCreate?.BRANCH?.value != getValues("branchCode")) {
        setLoadBranchByPromoCode(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMaturityByPromoCode, maturityStore]);
  // // load danh sách chi nhánh
  React.useEffect(() => {
    if (loadBranchByPromoCode === true && termStore) {
      onChangeBranch(termOpenStore.dataCreate?.BRANCH);
      setLoadBranchByPromoCode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadBranchByPromoCode]);

  return (
    <>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        {FieldForm}
        {FormInput}
      </Block>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        <Block w={200} />
        {FormCheckTerm}
      </Block>

      {ButtonForm}
    </>
  );
};

export default FormTermDeposit;
